import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import { Box } from '@/components/ui/box'
import { HStack } from '@/components/ui/hstack'
import { Spinner } from '@/components/ui/spinner'
import { Text } from '@/components/ui/text'
import TemplateCard from '@/modules/templates/TemplateCard'
import {
  fetchMoreTemplatesThunk,
  fetchTemplateTagCategoriesThunk,
  fetchTemplatesThunk,
  templateSelectors,
} from '@/redux/slices/templateSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { androidHeaderSearchBarThemeProps } from '@/theme'
import type { Tag, Template } from '@/typings/template'
import { SortTemplateBy } from '@/typings/template'
import { FlashList } from '@shopify/flash-list'
import { useTheme } from '@react-navigation/native'
import { Stack, useLocalSearchParams, useRouter } from 'expo-router'
import { debounce } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

const ALL_TAG = '全部'
const ALL_TAG_OBJECT: Tag = { id: -1, name: ALL_TAG, tagOrder: 0 }

export default function Templates() {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const dispatch = useAppDispatch()
  const searchParams = useLocalSearchParams<{ tag?: string }>()

  const templates = useAppSelector(templateSelectors.selectAll)
  const { hasNextPage, isLoading } = useAppSelector(state => state.templates.filteredTemplateState)
  const tagCategories = useAppSelector(state => state.templates.tagCategories)
  const loaded = useAppSelector(state => state.templates.filteredTemplateState.loaded)

  const [activeTag, setActiveTag] = useState<Tag>(() => {
    if (searchParams.tag && searchParams.tag !== ALL_TAG) {
      return { id: -2, name: searchParams.tag, tagOrder: 0 }
    }
    return ALL_TAG_OBJECT
  })

  const [searchText, setSearchText] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const isInitialMount = useRef(true)
  const hasInitializedFromUrl = useRef(false)
  const trimmedSearchText = useMemo(() => searchText.trim(), [searchText])

  const tags = useMemo(() => {
    const filteredTags = tagCategories
      .filter(group => group.name === '场景' || group.name === '行业')
      .flatMap(group => group.tags)
      .filter(tag => tag.name)
    return [ALL_TAG_OBJECT, ...filteredTags]
  }, [tagCategories])

  useEffect(() => {
    if (tags.length > 1 && activeTag.id === -2) {
      const realTag = tags.find(tag => tag.name === activeTag.name)
      if (realTag) {
        setActiveTag(realTag)
        hasInitializedFromUrl.current = true
      } else {
        setActiveTag(ALL_TAG_OBJECT)
        router.setParams({ tag: undefined })
        hasInitializedFromUrl.current = true
      }
    }
  }, [tags, activeTag, router])

  const debouncedSearch = useRef(
    debounce((keyword: string, tagNames: string[]) => {
      setIsSearching(true)
      dispatch(
        fetchTemplatesThunk({
          keywords: keyword || undefined,
          tags: tagNames,
          page: 1,
          size: 20,
          sortBy: SortTemplateBy.RELEVANCY,
        })
      ).finally(() => {
        setIsSearching(false)
      })
    }, 300)
  ).current

  const handleSearchTextChange = useCallback(
    (text: string) => {
      const trimmedNewSearchText = text.trim()

      setSearchText(text)

      if (trimmedNewSearchText === '' && trimmedSearchText === '') {
        return
      }

      const tagNames = activeTag.name === ALL_TAG ? [] : [activeTag.name]
      debouncedSearch(trimmedNewSearchText, tagNames)
    },
    [trimmedSearchText, debouncedSearch, activeTag.name]
  )

  useEffect(() => {
    dispatch(fetchTemplateTagCategoriesThunk())

    const tagNames = activeTag.name === ALL_TAG ? [] : [activeTag.name]
    dispatch(
      fetchTemplatesThunk({
        tags: tagNames,
        page: 1,
        size: 20,
        sortBy: SortTemplateBy.RELEVANCY,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (hasInitializedFromUrl.current) {
      hasInitializedFromUrl.current = false
      return
    }

    const tagNames = activeTag.name === ALL_TAG ? [] : [activeTag.name]
    dispatch(
      fetchTemplatesThunk({
        keywords: trimmedSearchText || undefined,
        tags: tagNames,
        page: 1,
        size: 20,
        sortBy: SortTemplateBy.RELEVANCY,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTag, dispatch])

  const handleTagChange = useCallback(
    (tag: Tag) => {
      setActiveTag(tag)
      if (tag.name === ALL_TAG) {
        router.setParams({ tag: undefined })
      } else {
        router.setParams({ tag: tag.name })
      }
    },
    [router]
  )

  const handleTemplatePress = useCallback(
    (template: Template) => {
      router.push(`/templates/${template.templateToken}`)
    },
    [router]
  )

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isLoading) {
      const tagNames = activeTag.name === ALL_TAG ? [] : [activeTag.name]
      dispatch(
        fetchMoreTemplatesThunk({
          keywords: trimmedSearchText || undefined,
          tags: tagNames,
          sortBy: SortTemplateBy.RELEVANCY,
        })
      )
    }
  }, [hasNextPage, isLoading, activeTag.name, trimmedSearchText, dispatch])

  const templateRows = useMemo(() => {
    const rows: (Template | null)[][] = []
    for (let i = 0; i < templates.length; i += 2) {
      rows.push([templates[i] ?? null, templates[i + 1] ?? null])
    }
    return rows
  }, [templates])

  const renderItem = useCallback(
    ({ item: row }: { item: (Template | null)[] }) => (
      <HStack space="xl" className="px-6 mb-6">
        <Box className="flex-1">{row[0] && <TemplateCard template={row[0]} onPress={handleTemplatePress} />}</Box>
        <Box className="flex-1">{row[1] && <TemplateCard template={row[1]} onPress={handleTemplatePress} />}</Box>
      </HStack>
    ),
    [handleTemplatePress]
  )

  const renderFooter = useCallback(() => {
    if (!isLoading || templates.length === 0) return null
    return (
      <View className="py-4 items-center">
        <ActivityIndicator size="small" />
      </View>
    )
  }, [isLoading, templates.length])

  if (!loaded && isLoading) {
    return (
      <View className="flex-1 items-center justify-center">
        <Spinner />
      </View>
    )
  }

  if (templates.length === 0) {
    return (
      <View className="flex-1 items-center justify-center">
        <Text size="body" className="text-secondary-label">暂无模板</Text>
      </View>
    )
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: '模板中心',
          headerSearchBarOptions: {
            placeholder: '搜索模板',
            hideWhenScrolling: false,
            ...androidHeaderSearchBarThemeProps(theme),
            onChangeText: (event: any) => {
              handleSearchTextChange(event.nativeEvent.text)
            },
          },
          ...buildScreenHeaderItems({
            right:
              tagCategories.length === 0
                ? []
                : [
                  {
                    type: 'menu',
                    label: activeTag.name,
                    menu: {
                      title: '选择分类',
                      items: tags.map(tag => ({
                        type: 'action',
                        label: tag.id === activeTag.id ? `✓ ${tag.name}` : tag.name,
                        onPress: () => handleTagChange(tag),
                      })),
                    },
                  },
                ],
          }),
        }}
      />

      {isSearching ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" />
          <Text size="subhead" className="mt-4 text-secondary-label">搜索中...</Text>
        </View>
      ) : templates.length === 0 && trimmedSearchText ? (
        <View className="flex-1 items-center justify-center">
          <Text size="subhead" className="text-secondary-label">未找到匹配的模板</Text>
          <Text size="subhead" className="mt-2 text-secondary-label">请尝试其他关键词</Text>
        </View>
      ) : (
        <FlashList
          data={templateRows}
          renderItem={renderItem}
          keyExtractor={(_item, index) => `row-${index}`}
          maintainVisibleContentPosition={{ disabled: true }}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.1}
          contentInsetAdjustmentBehavior="automatic"
          contentContainerStyle={{ paddingTop: 16, paddingBottom: insets.bottom + 16 }}
          ListFooterComponent={renderFooter}
        />
      )}
    </>
  )
}
